import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash, Pencil } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { MODULE_LABELS } from '../lib/permissions';
import { toast } from 'sonner';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'admin',            label: 'Admin (full access)' },
  { value: 'proposals_user',   label: 'Proposals Staff (Proposals & CRM)' },
  { value: 'attendance_user',  label: 'Attendance Staff (Attendance & Leaves)' },
  { value: 'staff',            label: 'Staff (My Workspace only)' },
  { value: 'team_lead',        label: 'Team Lead (My Workspace + Project Board)' },
  { value: 'project_coordinator', label: 'Project Coordinator (CSRM + Projects + Clients)' },
  { value: 'marketing_manager',   label: 'Marketing Manager (Full DMO + Approvals)' },
];

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));

const empty = () => ({ id: '', name: '', email: '', password: '', role: 'staff',
                       employee_id: '', is_team_lead: false, extra_modules: [],
                       can_view_attendance_monitor: true });

const UsersPage = () => {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const load = async () => {
    try {
      const [r, e] = await Promise.all([
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setList(r.data);
      setEmployees(e.data || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  // Re-fetch employees when the dialog opens (in case they were added/edited while page was idle)
  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/api/employees`, { withCredentials: true })
      .then((r) => setEmployees(r.data || []))
      .catch(() => {});
  }, [open]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (!q) return true;
      return `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q);
    });
  }, [list, search, roleFilter]);
  const { paged: pagedUsers, controls: userControls, startIdx: userStart } = usePagination(filteredUsers, { storageKey: 'users-page-size', testIdPrefix: 'user' });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (edit) {
        const payload = {
          name: form.name, role: form.role,
          employee_id: form.employee_id || '',
          is_team_lead: !!form.is_team_lead,
          extra_modules: form.extra_modules || [],
          can_view_attendance_monitor: !!form.can_view_attendance_monitor,
        };
        if (form.password) payload.password = form.password;
        await axios.put(`${API_URL}/api/users/${form.id}`, payload, { withCredentials: true });
        toast.success(`User "${form.name}" updated`);
      } else {
        await axios.post(`${API_URL}/api/users`, {
          name: form.name, email: form.email, password: form.password, role: form.role,
          employee_id: form.employee_id || '', is_team_lead: !!form.is_team_lead,
          extra_modules: form.extra_modules || [],
          can_view_attendance_monitor: !!form.can_view_attendance_monitor,
        }, { withCredentials: true });
        toast.success(`User "${form.name}" created`);
      }
      setOpen(false); setForm(empty()); setEdit(false); load();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Save failed: ${msg}`);
    } finally { setSaving(false); }
  };

  const onEdit = (u) => {
    setForm({
      id: u.id, name: u.name, email: u.email, password: '', role: u.role,
      employee_id: u.employee_id || '', is_team_lead: !!u.is_team_lead,
      extra_modules: Array.isArray(u.extra_modules) ? u.extra_modules : [],
      can_view_attendance_monitor: u.can_view_attendance_monitor !== false,  // legacy users default to true
    });
    setEdit(true); setOpen(true);
  };

  const toggleModule = (m) => {
    const cur = new Set(form.extra_modules || []);
    if (cur.has(m)) cur.delete(m); else cur.add(m);
    setForm({ ...form, extra_modules: Array.from(cur) });
  };
  const onDel = async (u) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email})?`)) return;
    try {
      await axios.delete(`${API_URL}/api/users/${u.id}`, { withCredentials: true });
      toast.success(`User "${u.name}" deleted`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>Users &amp; Roles</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Manage staff logins and what they can access</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty()); setEdit(false); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="text-white" style={{ background: 'var(--dx-primary)' }}>
              <Plus size={18} className="mr-1" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{edit ? 'Edit User' : 'Add User'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Full Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
              </div>
              <div><Label>Email *</Label>
                <Input type="email" required disabled={edit} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
                {edit && <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>}
              </div>
              <div><Label>Password {edit && <span className="text-xs text-gray-500">(leave blank to keep existing)</span>}{!edit && ' *'}</Label>
                <Input type="text" required={!edit} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
              </div>
              <div><Label>Role *</Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                  {ROLE_OPTIONS.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                </select>
              </div>
              <div><Label>Linked Employee <span className="text-xs text-gray-500">(for My Attendance / My Tasks)</span></Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  value={form.employee_id || ''} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  data-testid="user-employee">
                  <option value="">— Not linked —</option>
                  {employees.filter((e) => e.is_active !== false).map((e) => (
                    <option key={e.id} value={e.id}>{e.name} ({e.emp_id})</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  {employees.length === 0
                    ? 'Loading employees…'
                    : `${employees.filter((e) => e.is_active !== false).length} active employees available`}
                </p>
              </div>
              {form.role !== 'admin' && (
                <div className="border rounded-md p-3 bg-gray-50">
                  <Label className="text-xs font-semibold">Extra module access (optional)</Label>
                  <p className="text-[11px] text-gray-500 mb-2">Tick any additional modules this user should see on top of their primary role.</p>
                  <div className="flex flex-col gap-1.5">
                    {Object.entries(MODULE_LABELS).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={(form.extra_modules || []).includes(key)}
                          onChange={() => toggleModule(key)} data-testid={`module-${key}`} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {(form.role === 'staff' || form.role === 'team_lead' || form.role === 'proposals_user' || form.role === 'attendance_user') && (
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="user-is-lead" checked={!!form.is_team_lead}
                    onChange={(e) => setForm({ ...form, is_team_lead: e.target.checked })} data-testid="user-is-lead" />
                  <Label htmlFor="user-is-lead" className="text-sm cursor-pointer">Team Lead (sees own dept's tasks & standups)</Label>
                </div>
              )}
              {form.role !== 'admin' && (
                <div className="border rounded-md p-3 bg-amber-50/50 border-amber-200">
                  <div className="flex items-start gap-2">
                    <input type="checkbox" id="user-attendance-monitor"
                      className="mt-0.5"
                      checked={!!form.can_view_attendance_monitor}
                      onChange={(e) => setForm({ ...form, can_view_attendance_monitor: e.target.checked })}
                      data-testid="user-attendance-monitor" />
                    <div className="flex-1">
                      <Label htmlFor="user-attendance-monitor" className="text-sm cursor-pointer font-semibold">
                        Mobile: View Attendance Monitor
                      </Label>
                      <p className="text-[11px] text-gray-600 mt-0.5">
                        Allow this user to see the org-wide attendance snapshot (who&apos;s checked in, on break, etc.) in the mobile app. Enabled by default — toggle off to revoke.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-user-btn">
                  {saving ? 'Saving...' : (edit ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <div className="p-3 flex items-center gap-3 flex-wrap border-b">
          <Input placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 max-w-md" data-testid="user-search" />
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="text-sm px-2 py-2 border rounded bg-white" data-testid="user-role-filter">
            <option value="all">All Roles</option>
            {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Extra Access</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedUsers.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">{list.length === 0 ? 'No users yet' : 'No matches.'}</TableCell></TableRow>
            ) : pagedUsers.map((u, idx) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="text-gray-400">{userStart + idx + 1}</TableCell>
                <TableCell className="font-semibold">
                  {u.name}
                  {u.is_team_lead && <span className="ml-2 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">LEAD</span>}
                  {u.role !== 'admin' && u.can_view_attendance_monitor === false && (
                    <span className="ml-2 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Mobile attendance monitor access disabled" data-testid={`attmon-off-${u.id}`}>
                      ATT-MON OFF
                    </span>
                  )}
                </TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold"
                    style={{
                      background: u.role === 'admin' ? '#dcfce7' : u.role === 'proposals_user' ? '#ede9fe' : u.role === 'attendance_user' ? '#fef3c7' : '#e0f2fe',
                      color: u.role === 'admin' ? '#166534' : u.role === 'proposals_user' ? '#5b21b6' : u.role === 'attendance_user' ? '#92400e' : '#075985',
                    }}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(u.extra_modules || []).length === 0 && <span className="text-xs text-gray-400">—</span>}
                    {(u.extra_modules || []).map((m) => (
                      <span key={m} className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                        {MODULE_LABELS[m] || m}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => onEdit(u)} data-testid={`edit-user-${u.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="outline" className="ml-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => onDel(u)} data-testid={`del-user-${u.id}`}>
                    <Trash size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {userControls}
      </Card>
    </div>
  );
};

export default UsersPage;
